import * as React from 'react';
import { useConnectionTypes } from '../../../../../queries/ConnectionType/useConnectionTypes';
import {
	CONNECTION_TYPE_COLUMN_PREFIX,
	ColumnDefinition,
	ColumnId,
	useTableColumnVisibility,
} from '../hooks/useTableColumnVisibility';

// Excluded — already covered by the dedicated `contactOwner` column.
const OWNER_CONNECTION_TYPE = 'owner';

interface TableColumnVisibilityContextValue {
	columns: ColumnDefinition[];
	visibleColumns: ColumnDefinition[];
	visibleColumnCount: number;
	maxVisibleColumns: number;
	canAddMoreColumns: boolean;
	updateColumnVisibility: (columnId: ColumnId, isVisible: boolean) => void;
	resetToDefaults: () => void;
}

const TableColumnVisibilityContext = React.createContext<TableColumnVisibilityContextValue | null>(null);

export const useTableColumnVisibilityContext = (): TableColumnVisibilityContextValue => {
	const context = React.useContext(TableColumnVisibilityContext);
	if (!context) {
		throw new Error('useTableColumnVisibilityContext must be used within a TableColumnVisibilityProvider');
	}
	return context;
};

export const TableColumnVisibilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const connectionTypesQuery = useConnectionTypes();

	// One hidden-by-default column per connection type (minus Owner), keyed by the type name.
	const connectionTypeColumns = React.useMemo<ColumnDefinition[]>(() => {
		const availableConnectionTypes = connectionTypesQuery.data?.availableConnectionTypes ?? [];
		return availableConnectionTypes
			.filter(connectionType => !!connectionType.value && connectionType.value.toLowerCase() !== OWNER_CONNECTION_TYPE)
			.map(connectionType => ({
				id: `${CONNECTION_TYPE_COLUMN_PREFIX}${connectionType.value}` as ColumnId,
				label: connectionType.label,
				isRequired: false,
				isVisible: false,
				group: 'connections' as const,
			}));
	}, [connectionTypesQuery.data]);

	const value = useTableColumnVisibility(connectionTypeColumns);

	return <TableColumnVisibilityContext.Provider value={value}>{children}</TableColumnVisibilityContext.Provider>;
};
